import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { CreateOrganizationsPicker } from "../OrganizationsPicker";
import { CreatePropertiesPicker } from "../PropertiesPicker";
import { CreateClaimsPicker } from "../ClaimsPicker";
import { apiClient } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { CreateContactRequest, Contact } from "@/api/generated/client/models";

const EMPTY_FORM: CreateContactRequest = {
    contactType: "" as CreateContactRequest["contactType"],
    firstName: "",
    lastName: "",
};

interface ContactCreateDialogProps {
    open: boolean;
    onClose: () => void;
    createContact: (data: CreateContactRequest) => Promise<Contact>;
    isCreating: boolean;
    /** When set, newly created contact is auto-linked to these claims. */
    initialClaimIds?: number[];
    /** Hide the in-dialog claims picker (used when opened from a claim context). */
    hideClaimsPicker?: boolean;
    /** Called after a successful create (and link attempts). */
    onCreated?: () => void;
}

export function ContactCreateDialog({ open, onClose, createContact, initialClaimIds, hideClaimsPicker, onCreated }: ContactCreateDialogProps) {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [form, setForm] = useState<CreateContactRequest>({ ...EMPTY_FORM });
    const [pendingOrgIds, setPendingOrgIds] = useState<number[]>([]);
    const [pendingPropIds, setPendingPropIds] = useState<number[]>([]);
    const [pendingClaimIds, setPendingClaimIds] = useState<number[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Seed claim links when opened from a claim context.
    useEffect(() => {
        if (open && initialClaimIds && initialClaimIds.length > 0) {
            setPendingClaimIds(initialClaimIds);
        }
    }, [open, initialClaimIds]);

    const close = () => {
        onClose();
        setPendingOrgIds([]);
        setPendingPropIds([]);
        setPendingClaimIds([]);
        setForm({ ...EMPTY_FORM });
    };

    const handleCreate = async () => {
        setIsSubmitting(true);
        try {
            const created = await createContact(form);
            const contactId = created?.id;
            if (contactId) {
                const hasLinks = pendingOrgIds.length > 0 || pendingPropIds.length > 0 || pendingClaimIds.length > 0;
                const linkFailures: string[] = [];
                for (const orgId of pendingOrgIds) {
                    try {
                        await apiClient.contacts.linkContactOrganization({
                            id: contactId,
                            linkContactOrganizationRequest: { organizationId: orgId },
                        });
                    } catch { linkFailures.push(`Organization #${orgId}`); }
                }
                for (const propId of pendingPropIds) {
                    try {
                        await apiClient.contacts.linkContactProperty({
                            id: contactId,
                            linkContactPropertyRequest: { propertyId: propId },
                        });
                    } catch { linkFailures.push(`Property #${propId}`); }
                }
                for (const claimId of pendingClaimIds) {
                    try {
                        await apiClient.claims.linkClaimContact({
                            claimId,
                            linkClaimContactRequest: { contactId, role: form.contactType },
                        });
                    } catch { linkFailures.push(`Claim #${claimId}`); }
                }
                if (hasLinks) {
                    queryClient.invalidateQueries({ queryKey: ["contact-organizations", contactId] });
                    queryClient.invalidateQueries({ queryKey: ["contact-properties", contactId] });
                    queryClient.invalidateQueries({ queryKey: ["contact-claims", contactId] });
                    queryClient.invalidateQueries({ queryKey: ["organization-contacts"] });
                    queryClient.invalidateQueries({ queryKey: ["property-contacts"] });
                }
                if (linkFailures.length > 0) {
                    toast({
                        title: "Contact created, but some links failed",
                        description: `Failed to link: ${linkFailures.join(", ")}. You can retry from the contact detail page.`,
                        variant: "destructive",
                    });
                }
                onCreated?.();
            }
            close();
        } catch {
            // Error toast handled by mutation's onError callback
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="sm:max-w-lg max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto sm:overflow-auto">
                <DialogHeader>
                    <DialogTitle>Add Contact</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>First Name <span className="text-destructive">*</span></Label>
                            <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="First name" />
                        </div>
                        <div className="space-y-2">
                            <Label>Last Name <span className="text-destructive">*</span></Label>
                            <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} placeholder="Last name" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Contact Type <span className="text-destructive">*</span></Label>
                            <Input value={(form.contactType as string) || ""} onChange={(e) => setForm({ ...form, contactType: e.target.value as CreateContactRequest["contactType"] })} placeholder="e.g. Homeowner, Adjuster" />
                        </div>
                        <div className="space-y-2">
                            <Label>Job Title</Label>
                            <Input value={form.jobTitle || ""} onChange={(e) => setForm({ ...form, jobTitle: e.target.value || null })} placeholder="e.g. Senior Adjuster" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value || null })} placeholder="(555) 123-4567" />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value || null })} placeholder="name@example.com" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value || null })} placeholder="Additional notes..." rows={3} />
                    </div>

                    <Separator />

                    <CreateOrganizationsPicker
                        selectedIds={pendingOrgIds}
                        onChange={setPendingOrgIds}
                    />
                    <CreatePropertiesPicker
                        selectedIds={pendingPropIds}
                        onChange={setPendingPropIds}
                    />
                    {!hideClaimsPicker && (
                        <CreateClaimsPicker
                            selectedIds={pendingClaimIds}
                            onChange={setPendingClaimIds}
                        />
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={close} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={!form.firstName || !form.lastName || !form.contactType || isSubmitting}>
                        {isSubmitting ? "Creating..." : "Create Contact"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
